import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'
import Layout from './components/shared/Layout'
import Login from './components/Login'
import AttendanceManager from './components/attendance/clock-in-out/AttendanceManager'
import AttendanceLayout from './components/attendance/dtr/DailyTimeRecordLayout'
import ProtectedRoute from './helper/routes/ProtectedRoute'
import ReportComponent from './components/finance/ReportLayout'
import { UserProvider } from './helper/routes/UserContext'
import Unauthorized from './helper/UnauthorizedScreen'
import OTAttendanceManager from './components/attendance/ot/OTAttendanceManager'
import LeaveLayout from './components/attendance/leave/LeaveLayout'
import OvertimeRequestList from './components/management/OvertimeRequestList'
import EmployeeList from './components/employee/EmployeeList'
import EmployeeInfo from './components/employee/EmployeeInfo'
import UserRoleList from './components/role/UserRoleList'
import UserAccountList from './components/user-account/UserAccountList'
import UserAccountInfo from './components/user-account/UserAccountInfo'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import PositionList from './components/position/PositonList'
import AuditLogList from './components/audit-logs/AuditLogList'
import MyProfile from './components/shared/profile/MyProfile'
import LeaveDetails from './components/attendance/leave/LeaveDetails'
import LeaveCalendarList from './components/attendance/leave/LeaveCalendarList'
import LeaveDetailNoAction from './components/attendance/leave/LeaveDetailsNoAction'
import LeaveEmployeeListLayout from './components/attendance/leave/leave_config/LeaveEmployeeListLayout'
import LeaveApprovedCalendarList from './components/attendance/leave/LeaveApprovedCalendarList'
import InventoryList from './components/inventory/InventoryList'
import ItemList from './components/inventory/ItemList'
import CategoryList from './components/inventory/item-category/CategoryList'
import ItemAssignment from './components/inventory/ItemAssignment'
import ItemEmployeeAssignment from './components/inventory/ItemEmployeeAssignment'
import AcceptItem from './helper/AcceptItem'
import KioskList from './components/kiosk/KioskList'
import KioskLoginPage from './components/kiosk/device/KioskLoginPage'
import KioskReadyScreen from './components/kiosk/device/KioskReadyScreen'

function App() {
    const theme = createTheme({
        palette: {
            mode: 'light',
            primary: {
                main: '#d11919',
                dark: '#a01212',
                light: '#e15353',
                contrastText: '#ffffff'
            },
            secondary: {
                main: '#525866',
                dark: '#363b44',
                light: '#8a8f99',
                contrastText: '#ffffff'
            },
            background: {
                default: '#f7f7f8',
                paper: '#ffffff'
            },
            text: {
                primary: '#1f2125',
                secondary: '#6b7280'
            },
            divider: '#e5e7eb'
        },
        shape: {
            borderRadius: 8
        },
        typography: {
            fontFamily: ['Inter', 'system-ui', '-apple-system', 'Segoe UI', 'Roboto', 'sans-serif'].join(','),
            h1: { fontWeight: 700 },
            h2: { fontWeight: 700 },
            h3: { fontWeight: 700 },
            h4: { fontWeight: 700 },
            h5: { fontWeight: 600 },
            h6: { fontWeight: 600 },
            button: { fontWeight: 600, textTransform: 'none' }
        },
        components: {
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        textTransform: 'none',
                        fontWeight: 600
                    },
                    outlined: {
                        borderWidth: 1.5
                    }
                }
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none'
                    },
                    elevation1: {
                        boxShadow: 'none',
                        border: '1px solid #e5e7eb'
                    }
                }
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        boxShadow: 'none',
                        border: '1px solid #e5e7eb',
                        borderRadius: 12
                    }
                }
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        boxShadow: 'none',
                        borderBottom: '1px solid #e5e7eb'
                    }
                }
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        fontWeight: 600,
                        borderRadius: 6
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 8
                    }
                }
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        // Only style the shrunk (notched/floating) state — matches the
                        // app's uppercase/tracked-out .form-label style. The resting state
                        // is left as MUI default since it's acting as placeholder-sized text
                        // there, and uppercase+tracking would look oversized at that scale.
                        // Color is intentionally untouched so focused/error red still works.
                        '&.MuiInputLabel-shrink': {
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em'
                        }
                    }
                }
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 12,
                        boxShadow: '0 10px 40px rgba(0,0,0,0.12)'
                    }
                }
            },
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        '--DataGrid-containerBackground': '#fafafa'
                    },
                    columnHeaders: {
                        backgroundColor: '#fafafa',
                        borderBottom: '1px solid #e5e7eb'
                    },
                    columnHeaderTitle: {
                        fontWeight: 700
                    }
                }
            },
            MuiTableCell: {
                styleOverrides: {
                    head: {
                        fontWeight: 700,
                        backgroundColor: '#fafafa'
                    }
                }
            }
        }
    })

    return (
        <UserProvider>
            <ThemeProvider theme={theme}>
                <Router>
                    <Routes>
                        <Route path="login" element={<Login />} /> {/* Public route */}
                        {/* <Route path="webAuthApp" element={<WebAuthApp />} /> 
                        <Route path="attendance/scan" element={<QRAttendanceActions />} />  */}
                        <Route
                            path="/"
                            element={
                                <ProtectedRoute allowedRoles={['super admin', 'employee', 'finance', 'management']}>
                                    <Layout />
                                </ProtectedRoute>
                            }
                        >
                            <Route
                                index
                                element={
                                    <ProtectedRoute allowedRoles={['super admin', 'employee', 'finance', 'management']}>
                                        <AttendanceManager />
                                    </ProtectedRoute>
                                }
                            />
                            {/* Protected route */}
                            <Route
                                path="attendance/dtr"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin', 'employee', 'finance', 'management']}>
                                        <AttendanceLayout />
                                    </ProtectedRoute>
                                }
                            />
                            {/* Protected route */}
                            <Route
                                path="attendance/ot"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin', 'employee', 'finance', 'management']}>
                                        <OTAttendanceManager />
                                    </ProtectedRoute>
                                }
                            />
                            {/* Protected route */}
                            <Route
                                path="attendance/leave"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin', 'employee', 'finance', 'management']}>
                                        <LeaveLayout />
                                        {/* <ComingSoon /> */}
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="leave/:leaveId"
                                element={
                                    <ProtectedRoute allowedRoles={['management', 'super admin', 'finance']}>
                                        <LeaveDetails />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="leave/details/:leaveId"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin', 'employee', 'finance', 'management']}>
                                        <LeaveDetailNoAction />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/leave/leave-approval"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin', 'management', 'finance']}>
                                        <LeaveCalendarList />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/leave/leave-approved"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin', 'management', 'finance']}>
                                        <LeaveApprovedCalendarList />
                                    </ProtectedRoute>
                                }
                            />
                            {/* Protected route */}
                            <Route
                                path="management/overtime-approval"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin', 'management']}>
                                        <OvertimeRequestList />
                                    </ProtectedRoute>
                                }
                            />
                            {/* Protected route */}
                            <Route
                                path="finance/generate-dtr"
                                element={
                                    <ProtectedRoute allowedRoles={['finance']}>
                                        <ReportComponent />
                                    </ProtectedRoute>
                                }
                            />
                            {/* Protected route */}
                            <Route
                                path="employee"
                                element={
                                    <ProtectedRoute allowedRoles={['finance', 'super admin', 'management']}>
                                        <EmployeeList />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="employee/info/:employeeId"
                                element={
                                    <ProtectedRoute allowedRoles={['finance', 'super admin', 'management']}>
                                        <EmployeeInfo />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="roles"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin']}>
                                        <UserRoleList />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="user-account"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin']}>
                                        <UserAccountList />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="account/info/:username"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin']}>
                                        <UserAccountInfo />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="audit-trail"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin']}>
                                        <AuditLogList />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="profile/info"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin', 'employee', 'finance', 'management']}>
                                        <MyProfile />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="leave/employee/list"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin', 'finance', 'management']}>
                                        <LeaveEmployeeListLayout />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="inventory"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin']}>
                                        <InventoryList />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="inventory/items"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin']}>
                                        <ItemList />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="categories/items"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin']}>
                                        <CategoryList />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="inventory/assigned-items"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin']}>
                                        <InventoryList />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="inventory/assign-item"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin']}>
                                        <ItemAssignment />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="inventory/employee/assigned-items"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin', 'finance', 'management', 'employee']}>
                                        <ItemEmployeeAssignment />
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="positions" element={<PositionList />} /> {/* Public route */}
                            <Route
                                path="kiosks"
                                element={
                                    <ProtectedRoute allowedRoles={['super admin']}>
                                        <KioskList />
                                    </ProtectedRoute>
                                }
                            />
                        </Route>
                        <Route path="unauthorized" element={<Unauthorized />} /> {/* Display unauthorized message */}
                        <Route path="api-inventory/item/accept" element={<AcceptItem />} />{' '}
                        {/* Display accept item message */}
                        {/* Kiosk device routes — separate kiosk-token auth, not staff ProtectedRoute */}
                        <Route path="kiosk/login" element={<KioskLoginPage />} />
                        <Route path="kiosk" element={<KioskReadyScreen />} />
                    </Routes>
                </Router>
            </ThemeProvider>
        </UserProvider>
    )
}

export default App
